import { ReactNode, useRef, useState } from 'react';
import dayjs from 'dayjs';
import type { OpsContext } from './useOpsContext';

/**
 * 브리핑에서 선물을 보내는 흐름 — **화면이 붙잡는 얇은 손잡이**.
 *
 * ─────────────────────────────────────────────────────────────
 * 왜 창을 따로 떼어 눌렀을 때 불러오나
 * ─────────────────────────────────────────────────────────────
 * 확인 창(BriefingGiftModal)은 기프티콘 조회·발송 API 와 2차 비밀번호 인증을
 * 끌고 옵니다. 그 훅들을 브리핑 화면 최상단에 두면
 *
 *   · 매일 아침 브리핑을 여는 것만으로 기프티콘 쪽이 함께 붙고
 *     (선물을 보낼 수 없는 계정에서는 403 이 찍힙니다)
 *   · 브리핑 화면 전체가 기프티콘·인증 모듈에 묶입니다.
 *
 * 그래서 이 파일에는 **상태와 판단만** 두고, 실제 창은 원장이
 * 「선물 보내기」를 누른 순간 불러옵니다. 불러오지 못하면 그 사실을 그대로 말합니다.
 *
 * 실제 발송 규약(멱등키·결과 4상태·잠금 재검사)은 BriefingGiftModal.tsx 에 있습니다.
 */

export type GiftIntent = {
    /** 수신자 — 브리핑 항목의 subjectId / 회원 카드의 memberId (둘 다 Member 식별자) */
    memberId: string;
    memberName: string;
    /** 화면이 보여준 판단 문장 — 발송 사유(reason)로 그대로 싣는다 */
    summary: string;
    /** 화면이 보고 있는 날짜 (YYYY-MM-DD) — 멱등키의 날짜이자 '오늘' 검사 대상 */
    dateKey: string;
};

export const isTodayKey = (dateKey: string) => dateKey === dayjs().format('YYYY-MM-DD');

export type GiftSendFlow = {
    /** 확인 창을 연다 — 선물은 여기를 지나야만 나간다 */
    open: (intent: GiftIntent) => void;
    /** 화면 맨 아래에 그대로 놓으면 된다 (2차 비밀번호 창까지 이 안에 있다) */
    modal: ReactNode;
    /** 서버 왕복 중인가 — 목록 버튼을 함께 잠글 때 쓴다 */
    sending: boolean;
    /** 이 계정으로 선물을 보낼 수 있나 (아니면 버튼을 아예 그리지 않는다) */
    allowed: boolean;
    /**
     * 이 회원에게 **이번에** 보낸 선물 — 카드에 남길 문장 (없으면 null).
     *
     * ⚠️ 새로고침하면 사라진다. 서버 발송 내역에 수신자 id 가 없어
     *    (GiftHistoryItem 에 receiverId 없음) 복원할 방법이 없다.
     *    이중 발송은 멱등키가 서버에서 막으므로 이 배지는 **UX 보조**다.
     *    복원 계약은 BACKEND_HANDOFF_OPS_BRIEFING_GIFT.md 에 적어 두었다.
     */
    sentGiftFor: (memberId: string) => string | null;
};

/** 눌렀을 때 불러온 확인 창 모듈 */
type GiftModule = typeof import('./BriefingGiftModal');

export const useGiftSendFlow = (
    ctx: OpsContext,
    { notify, onOpenHistory }: { notify: (message: string) => void; onOpenHistory?: () => void },
): GiftSendFlow => {
    const [intent, setIntent] = useState<GiftIntent | null>(null);
    const [sending, setSending] = useState(false);
    const [module, setModule] = useState<GiftModule | null>(null);
    /** memberId → 이번에 보낸 선물 한 줄 */
    const [sentGifts, setSentGifts] = useState<Record<string, string>>({});
    /**
     * memberId → 직전에 **보내려 한** 상품. 다시 열었을 때 그대로 잡아 주는 편의다.
     *
     * (멱등키에는 상품이 들어 있지 않다 — giftSendKey.ts. 이 기억이 사라져도
     *  키는 달라지지 않는다. 원장이 아까 고른 것을 다시 찾지 않아도 되게 할 뿐이다)
     */
    const [lastProductByMember, setLastProductByMember] = useState<Record<string, string>>({});
    /** 창을 불러오는 중 두 번 누르는 것을 막는다 */
    const loadingRef = useRef(false);

    const allowed = ctx.canSendGift === true;

    /**
     * 확인 창을 연다.
     *
     * 여기서 하는 검사는 **동기적으로 알 수 있는 것뿐**이다 (계정·회원·날짜·센터).
     * 잠금·잔액·상품은 창 안에서 본다 — 서버 왕복이 필요하기 때문이다.
     * 날짜는 **보낼 때 한 번 더** 본다 (창을 열어 둔 채 자정을 넘길 수 있다).
     */
    const open = (next: GiftIntent) => {
        if (!allowed) return;
        if (sending || loadingRef.current) return;
        if (!next.memberId) {
            notify('회원 정보가 연결되지 않아 선물을 보낼 수 없습니다');
            return;
        }
        if (!next.dateKey || !isTodayKey(next.dateKey)) {
            notify('지난 날짜에서는 선물을 보낼 수 없습니다 · 오늘 날짜로 이동해 주세요');
            return;
        }
        if (!ctx.centerId) {
            notify('센터 정보를 확인하지 못해 선물을 보낼 수 없습니다');
            return;
        }
        if (module) {
            setIntent(next);
            return;
        }
        loadingRef.current = true;
        import('./BriefingGiftModal')
            .then((loaded) => {
                loadingRef.current = false;
                setModule(loaded);
                setIntent(next);
            })
            .catch(() => {
                loadingRef.current = false;
                // 청크를 못 받았다 — 창이 열리지 않은 이유를 말한다 (조용히 삼키지 않는다)
                notify('선물 창을 여는 데 실패했습니다. 새로고침 후 다시 시도해 주세요');
            });
    };

    const Session = module?.GiftSendSession;
    const modal =
        intent && Session ? (
            <Session
                /* 회원이 바뀌면 세션을 새로 만든다 */
                key={`${intent.memberId}:${intent.dateKey}`}
                ctx={ctx}
                intent={intent}
                preferredProductId={lastProductByMember[intent.memberId]}
                notify={notify}
                onSendingChange={setSending}
                onSent={(memberId: string, label: string) =>
                    setSentGifts((current) => ({ ...current, [memberId]: label }))
                }
                onProductUsed={(memberId: string, productId: string) =>
                    setLastProductByMember((current) => ({ ...current, [memberId]: productId }))
                }
                onOpenHistory={onOpenHistory}
                onClose={() => {
                    setSending(false);
                    setIntent(null);
                }}
            />
        ) : null;

    return {
        open,
        modal,
        sending,
        allowed,
        sentGiftFor: (memberId: string) => sentGifts[memberId] ?? null,
    };
};
